import React, { useState, useMemo } from 'react';
import { Modal, Input, Checkbox, Button, Spin } from 'antd-v5';
import { SearchOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import './TagSelectionModal.scss';

/**
 * TagSelectionModal component for selecting tags in a modal dialog
 * Matches Figma design with search, checkbox list, and Cancel/Ok buttons
 */
const TagSelectionModal = ({ open, onClose, onConfirm, tags, selectedTagIds, isLoading, title, subtitle, excludeTagIds }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [localSelectedIds, setLocalSelectedIds] = useState(selectedTagIds);

  // Reset local selection when modal opens
  React.useEffect(() => {
    if (open) {
      setLocalSelectedIds(selectedTagIds);
      setSearchTerm('');
    }
  }, [open, selectedTagIds]);

  // Filter tags based on search term and excluded IDs
  const filteredTags = useMemo(() => {
    let result = tags;
    if (excludeTagIds?.length > 0) {
      result = result.filter(tag => !excludeTagIds.includes(tag.id));
    }
    if (!searchTerm.trim()) return result;
    const lowerSearch = searchTerm.toLowerCase();
    return result.filter(tag => tag.name?.toLowerCase().includes(lowerSearch));
  }, [tags, searchTerm, excludeTagIds]);

  const handleCheckboxChange = (tagId, checked) => {
    if (checked) {
      setLocalSelectedIds(prev => [...prev, tagId]);
    } else {
      setLocalSelectedIds(prev => prev.filter(id => id !== tagId));
    }
  };

  const handleConfirm = () => {
    onConfirm(localSelectedIds);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={title}
      footer={null}
      className="tag-selection-modal"
      width={480}
      centered
    >
      <p className="tag-selection-modal__subtitle">{subtitle}</p>

      <Input
        placeholder="Search"
        prefix={<SearchOutlined />}
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        className="tag-selection-modal__search"
      />

      <div className="tag-selection-modal__list">
        {isLoading ? (
          <div className="tag-selection-modal__loading">
            <Spin size="small" />
          </div>
        ) : filteredTags.length === 0 ? (
          <div className="tag-selection-modal__empty">No tags found</div>
        ) : (
          filteredTags.map(tag => (
            <div key={tag.id} className="tag-selection-modal__item">
              <Checkbox
                checked={localSelectedIds.includes(tag.id)}
                onChange={e => handleCheckboxChange(tag.id, e.target.checked)}
              >
                {tag.name}
              </Checkbox>
            </div>
          ))
        )}
      </div>

      <div className="tag-selection-modal__footer">
        <Button onClick={handleCancel}>Cancel</Button>
        <Button type="primary" onClick={handleConfirm}>
          Ok
        </Button>
      </div>
    </Modal>
  );
};

TagSelectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string,
    }),
  ),
  selectedTagIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  excludeTagIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

TagSelectionModal.defaultProps = {
  tags: [],
  selectedTagIds: [],
  isLoading: false,
  title: 'Select Tags',
  subtitle: 'Adding more tags will expand the audience for your event.',
  excludeTagIds: [],
};

export default TagSelectionModal;
